import { API_URL } from './constants'
import { normalizeOutpoint } from './item-listing'

/** What the ownership index says about an item right now. */
export interface LiveItemOutpoint {
	owner: string
	/** `txid:vout` of the output holding the inscription. */
	outpoint: string
	/** The whole value of that output: it all moves with the inscription. */
	sats: number
}

export class LiveItemUnavailable extends Error {
	constructor(message = 'The item’s current output could not be checked. Try again.') {
		super(message)
		this.name = 'LiveItemUnavailable'
	}
}

/**
 * The live outpoint, owner and output value of an inscription, read past
 * every cache (no-store plus a cache-buster, since a CDN or a stale SWR entry
 * would hand back an output the item has already left). Throws
 * LiveItemUnavailable on a failed request or a body without a usable owner,
 * outpoint and positive value, so callers can tell "loading" from "loaded
 * but unusable" and offer a retry for both failures.
 */
export async function fetchLiveItemOutpoint(
	inscriptionId: string,
	base: string = API_URL,
	init: { signal?: AbortSignal } = {}
): Promise<LiveItemOutpoint> {
	let data: any
	try {
		const response = await fetch(
			`${base}/inscription/${encodeURIComponent(inscriptionId)}/outpoint?t=${Date.now()}`,
			{ cache: 'no-store', signal: init.signal }
		)
		if (!response.ok) throw new Error(`http_${response.status}`)
		data = await response.json()
	} catch (error) {
		if ((error as { name?: string })?.name === 'AbortError') throw error
		throw new LiveItemUnavailable()
	}
	const outpoint = normalizeOutpoint(data?.inscription?.outpoint)
	const sats = Number(data?.sats ?? data?.inscription?.sats)
	if (typeof data?.owner !== 'string' || !data.owner || !outpoint || !Number.isSafeInteger(sats) || sats <= 0)
		throw new LiveItemUnavailable('The item’s current output could not be read. Try again.')
	return { owner: data.owner, outpoint, sats }
}
